import { useEffect, useState } from "react";

const SESSION_KEY = "meta_fbclid";

export interface CapturedFbclid {
  fbclid: string;
  /** Milliseconds when the identifier was observed. Meta expects this inside fbc. */
  capturedAt: number;
}

/**
 * Fallback for when sessionStorage is unavailable. Inside an embedded iframe the browser
 * may block storage (third-party storage partitioning). This value then keeps the
 * identifier at least for the duration of the page session.
 */
let inMemory: CapturedFbclid | undefined;

const readStored = (): CapturedFbclid | undefined => {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as CapturedFbclid) : inMemory;
  } catch {
    return inMemory;
  }
};

const store = (captured: CapturedFbclid): void => {
  inMemory = captured;
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(captured));
  } catch {
    // Blocked storage must never affect the page - the in-memory value is enough.
  }
};

const captureFromUrl = (): void => {
  if (typeof window === "undefined") return;

  const fromUrl = new URLSearchParams(window.location.search).get("fbclid");
  // Only overwrite when the identifier actually changed, otherwise every navigation
  // would reset the capture time and fbc would drift towards the submission time.
  if (fromUrl && readStored()?.fbclid !== fromUrl) {
    store({ fbclid: fromUrl, capturedAt: Date.now() });
  }
};

/**
 * Capture on bundle load, not when the form mounts.
 *
 * Otherwise the identifier would be lost in the common case: the applicant lands on the
 * job list with `?fbclid=...`, clicks through to the detail page, and that navigation
 * replaces the URL including the parameter. Reading only when the form mounts would find
 * nothing left.
 */
captureFromUrl();

/**
 * Meta appends the URL parameter `fbclid` to every ad click. It is captured on the first
 * page load together with the time it was seen, and held for the session so the way from
 * the job list to the detail page does not lose it.
 *
 * Deliberately sessionStorage only: the identifier should disappear with the tab. It is
 * passed to the backend for the conversion report and is not stored there.
 */
export const useFbclid = (): CapturedFbclid | undefined => {
  const [captured, setCaptured] = useState<CapturedFbclid | undefined>();

  useEffect(() => {
    // Try again in case the user lands on a subpage with a new identifier without the
    // bundle being reloaded.
    captureFromUrl();
    setCaptured(readStored());
  }, []);

  return captured;
};
